if (!customElements.get('ikaya-stories-carousel')) {
  class IkayaStoriesCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.cards = Array.from(this.track.querySelectorAll('.mundos-card'));
      this.startDrag = this.startDrag.bind(this);
      this.drag = this.drag.bind(this);
      this.endDrag = this.endDrag.bind(this);
      this.preventClickAfterDrag = this.preventClickAfterDrag.bind(this);
      this.preventNativeImageDrag = this.preventNativeImageDrag.bind(this);
      this.requestCardEffects = this.requestCardEffects.bind(this);

      this.track.addEventListener('scroll', this.requestCardEffects, { passive: true });
      this.track.addEventListener('dragstart', this.preventNativeImageDrag);
      this.resizeObserver = new ResizeObserver(this.requestCardEffects);
      this.resizeObserver.observe(this.track);
      this.requestCardEffects();

      if (!window.matchMedia('(pointer: fine)').matches) return;

      this.track.addEventListener('pointerdown', this.startDrag);
      this.track.addEventListener('pointermove', this.drag);
      this.track.addEventListener('pointerup', this.endDrag);
      this.track.addEventListener('pointercancel', this.endDrag);
      this.track.addEventListener('lostpointercapture', this.endDrag);
      this.track.addEventListener('click', this.preventClickAfterDrag, true);
    }

    disconnectedCallback() {
      if (!this.track) return;

      this.track.removeEventListener('scroll', this.requestCardEffects);
      this.track.removeEventListener('dragstart', this.preventNativeImageDrag);
      this.track.removeEventListener('pointerdown', this.startDrag);
      this.track.removeEventListener('pointermove', this.drag);
      this.track.removeEventListener('pointerup', this.endDrag);
      this.track.removeEventListener('pointercancel', this.endDrag);
      this.track.removeEventListener('lostpointercapture', this.endDrag);
      this.track.removeEventListener('click', this.preventClickAfterDrag, true);
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.momentumFrame);
      cancelAnimationFrame(this.effectsFrame);
      window.clearTimeout(this.clickGuardTimer);
    }

    startDrag(event) {
      if (event.button !== 0) return;

      cancelAnimationFrame(this.momentumFrame);
      window.clearTimeout(this.clickGuardTimer);
      this.isPointerDown = true;
      this.dragged = false;
      this.startX = event.clientX;
      this.lastX = event.clientX;
      this.lastTime = event.timeStamp;
      this.velocity = 0;
    }

    drag(event) {
      if (!this.isPointerDown) return;

      const distance = event.clientX - this.lastX;
      const elapsed = Math.max(event.timeStamp - this.lastTime, 1);

      if (!this.dragged && Math.abs(event.clientX - this.startX) > 4) {
        this.dragged = true;
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
      }

      this.track.scrollLeft -= distance;
      this.velocity = (-distance / elapsed) * 0.82;
      this.lastX = event.clientX;
      this.lastTime = event.timeStamp;
      this.requestCardEffects();
    }

    endDrag(event) {
      if (!this.isPointerDown) return;

      this.isPointerDown = false;
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);

      if (this.dragged) {
        this.clickGuardTimer = window.setTimeout(() => {
          this.dragged = false;
        }, 0);
        this.startMomentum();
      } else {
        this.finishDrag();
      }
    }

    startMomentum() {
      const step = () => {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        this.track.scrollLeft += this.velocity * 16;
        this.velocity *= 0.945;
        this.requestCardEffects();

        if (this.track.scrollLeft <= 0 || this.track.scrollLeft >= maxScroll || Math.abs(this.velocity) < 0.025) {
          this.finishDrag();
          return;
        }

        this.momentumFrame = requestAnimationFrame(step);
      };

      this.momentumFrame = requestAnimationFrame(step);
    }

    finishDrag() {
      this.track.classList.remove('is-dragging');
      this.requestCardEffects();
    }

    requestCardEffects() {
      if (this.effectsFrame) return;

      this.effectsFrame = requestAnimationFrame(() => {
        this.effectsFrame = null;
        this.updateCardEffects();
      });
    }

    updateCardEffects() {
      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;

      this.cards.forEach((card) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = (cardCenter - trackCenter) / ((trackRect.width + cardRect.width) / 2);
        const progress = Math.max(-1, Math.min(1, distance));
        const intensity = Math.abs(progress);

        card.style.setProperty('--mundos-card-opacity', `${1 - intensity * 0.22}`);
      });
    }

    preventClickAfterDrag(event) {
      if (!this.dragged) return;

      event.preventDefault();
      event.stopPropagation();
      this.dragged = false;
    }

    preventNativeImageDrag(event) {
      if (event.target.matches('.mundos-card img')) event.preventDefault();
    }
  }

  customElements.define('ikaya-stories-carousel', IkayaStoriesCarousel);
}
